import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, FilterList as FilterIcon } from '@mui/icons-material';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../../Components/ServerSideTable';
import {
  getWorkflowIntroductions,
  WorkflowListChapter,
  WorkflowListItem,
} from '../../../endpoints/introductionWorkflow';
import { SortOrder } from '../../../types/table';
import { useApiGet } from '../../../utils/httpClientTs';

const INTRO_CLOSED = 'c3a81e04b6d24f0a9e7c1b5d8a2f4062';
const INTRO_INITIATED = 'c3a81e04b6d24f0a9e7c1b5d8a2f4063';
const CHAPTER_REMOVED = 'c3a81e04b6d24f0a9e7c1b5d8a2f4065';
const CHAPTER_ACCEPTED = 'c3a81e04b6d24f0a9e7c1b5d8a2f4066';

const STATUS_OPTIONS = [
  { value: 'c3a81e04b6d24f0a9e7c1b5d8a2f4061', label: 'Open' },
  { value: INTRO_CLOSED, label: 'Closed' },
  { value: INTRO_INITIATED, label: 'Initiated' },
];

function renderChapters(
  chapters: WorkflowListChapter[] | undefined,
  row: WorkflowListItem
) {
  if (!chapters || chapters.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No chapters
      </Typography>
    );
  }
  const introDone =
    row.status_id === INTRO_CLOSED || row.status_id === INTRO_INITIATED;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {chapters.map((chapter) => {
        let chipColor = '#57B9FF';
        if (
          introDone ||
          chapter.contact_made ||
          chapter.status_id === CHAPTER_REMOVED ||
          chapter.status_id === CHAPTER_ACCEPTED
        ) {
          chipColor = '#00A859';
        }
        return (
          <Chip
            key={chapter.id}
            label={chapter.name}
            size="small"
            sx={{
              backgroundColor: chipColor,
              color: 'text.primary',
              '&:hover': { backgroundColor: chipColor },
            }}
          />
        );
      })}
    </Box>
  );
}

const columns: ServerSideTableColumn<WorkflowListItem>[] = [
  { id: 'name', label: 'Name', width: '20%', sortable: false },
  { id: 'status_name', label: 'Status', width: '20%', sortable: false },
  { id: 'created_at', label: 'Created At', width: '20%', sortable: false },
  {
    id: 'chapters',
    label: 'Local Chapters',
    width: '40%',
    sortable: false,
    render: (chapters: WorkflowListChapter[] | undefined, row) =>
      renderChapters(chapters, row),
  },
];

const IntroductionWorkflowList: React.FC = () => {
  const navigate = useNavigate();
  const [order] = useState<SortOrder>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [delaySearch, setDelaySearch] = useState('');
  const [statusId, setStatusId] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearch(delaySearch);
      setPage(0);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delaySearch]);

  const { data, isFetching, error } = useApiGet(
    ['introduction-workflow', rowsPerPage, page, search, statusId],
    () =>
      getWorkflowIntroductions({
        page: page + 1,
        per_page: rowsPerPage,
        search: search || undefined,
        status_id: statusId || undefined,
      })
  );

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Error loading introductions: {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12} padding={1}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4">Non-Member Intro</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/introductions/create')}
            sx={{ minWidth: 200 }}
          >
            Add Introduction
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12} padding={1}>
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search"
                size="small"
                value={delaySearch}
                onChange={(event) => setDelaySearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setStatusId('');
                  setSearch('');
                  setDelaySearch('');
                  setPage(0);
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
          {showFilters && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusId}
                      label="Status"
                      onChange={(event) => {
                        setStatusId(event.target.value);
                        setPage(0);
                      }}
                    >
                      <MenuItem value="">
                        <em>All Statuses</em>
                      </MenuItem>
                      {STATUS_OPTIONS.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} padding={1}>
        <ServerSideTable
          data={data?.data || []}
          columns={columns}
          loading={isFetching}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={data?.pagination?.total_count || 0}
          totalPages={data?.pagination?.total_pages || 0}
          hasNext={data?.pagination?.has_next || false}
          hasPrev={data?.pagination?.has_prev || false}
          order={order}
          orderBy="created_at"
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setPage(0);
          }}
          onSort={() => undefined}
          onRowClick={(row) => navigate(`/introductions/${row.id}`)}
          emptyMessage="No introductions found"
        />
      </Grid>
    </Grid>
  );
};

export default IntroductionWorkflowList;
